'use client'

import type { ReactNode } from 'react'

import type { SiteSettings } from '@typings'
import { CartContextProvider, useCart } from '@contexts/cart.context'
import { SettingsContextProvider, useSettings } from '@contexts/settings.context'
import { UiContextProvider, useUi } from '@contexts/ui.context'
import { UserContextProvider, useUser } from '@contexts/user.context'
import { useWishlist, WishlistContextProvider } from '@contexts/wishlist.context'

import type { EntityCartSettingsQueryResult } from '@/sanity/generated-types'

export interface RootContextProviderProps {
  children: ReactNode
  // First path segment — the destination slug from the URL, or null on non-destination pages
  destination: string | null
  // Resolved destination slug from Sanity (null when no destination matched)
  site: string | null
  userData: { ua: string | null; ip: string | null; hashedIp: string | null }
  GTM_ID: string
  settings: SiteSettings
  isFamilySite: boolean
  translations: Array<{ key: string; value: string }>
  cartSettings: EntityCartSettingsQueryResult
  enableWishlist: boolean
}

// Combine Context Components (or Providers) util
export const CombineContextComponents = ({
  destination,
  site,
  userData,
  GTM_ID,
  settings,
  isFamilySite,
  children,
  translations,
  cartSettings,
  enableWishlist,
}: RootContextProviderProps) => {
  return (
    <UiContextProvider>
      <CartContextProvider
        GTM_ID={GTM_ID}
        destination={site}
        isFamilySite={isFamilySite}
        cartSettings={cartSettings}>
        <WishlistContextProvider
          GTM_ID={GTM_ID}
          destination={destination}
          isFamilySite={isFamilySite}
          enableWishlistProp={enableWishlist}>
          <SettingsContextProvider
            isFamilySite={isFamilySite}
            GTM_ID={GTM_ID}
            settings={settings}
            translations={translations}>
            <UserContextProvider
              GTM_ID={GTM_ID}
              userData={userData}
              destination={site}
              isFamilySite={isFamilySite}>
              {children}
            </UserContextProvider>
          </SettingsContextProvider>
        </WishlistContextProvider>
      </CartContextProvider>
    </UiContextProvider>
  )
}

// Context Providers collection
export const RNXTMultiContextCollection = {
  cart: CartContextProvider,
  wishlist: WishlistContextProvider,
  ui: UiContextProvider,
  settings: SettingsContextProvider,
  user: UserContextProvider,
}

// Context Stores collection
export const RNXTMultiContextStoreCollection = {
  cart: useCart,
  wishlist: useWishlist,
  settings: useSettings,
  ui: useUi,
  user: useUser,
}

export const RNXTContextProvider = ({
  destination,
  site,
  userData,
  GTM_ID,
  settings,
  isFamilySite,
  children,
  translations,
  enableWishlist,
  cartSettings,
}: RootContextProviderProps) => {
  return (
    <CombineContextComponents
      destination={destination}
      site={site}
      userData={userData}
      GTM_ID={GTM_ID}
      isFamilySite={isFamilySite}
      settings={settings}
      translations={translations}
      enableWishlist={enableWishlist}
      cartSettings={cartSettings}>
      {children}
    </CombineContextComponents>
  )
}

RNXTContextProvider.displayName = 'RNXTContextProvider'
